// TeachingEmptyState — the teaching version of an honest empty state:
// status + one learning cue + optional primary action + "Learn more" into the
// contextual help drawer. Pass it as QueryStateGate's `empty` prop so it only
// renders on confirmed success (never as a lie over pending/paused/error):
//
//   <QueryStateGate query={runsQ} noun="runs"
//     empty={<TeachingEmptyState icon={Package} status="No runs yet"
//       cue="A run starts when fruit is tipped against a delivery."
//       action={<button …>Go to Tipping →</button>} />} />
//
// "Learn more" appears automatically when the route (or an explicit topicId)
// has a help topic in src/help/topics/ — it re-teaches every rotating
// kiosk/seasonal user at zero marginal cost, fully offline.

import { ReactNode, useState } from 'react'
import { HelpCircle, type LucideIcon } from 'lucide-react'
import clsx from 'clsx'
import { useLocation } from 'react-router-dom'
import { HelpDrawer } from '@/components/HelpButton'
import { hasHelpTopic, resolveHelpTopicId } from '@/lib/helpTopics'

export default function TeachingEmptyState({ icon: Icon, status, cue, action, topicId, className }: {
  icon?:   LucideIcon
  /** The honest claim — "No deliveries", "No runs yet". Shown bold. */
  status:  string
  /** One learning sentence: what this screen is for / what makes rows appear. */
  cue:     string
  /** Optional primary action node (button/link), rendered as-is below the cue. */
  action?: ReactNode
  /** Help topic for "Learn more". Defaults to the current route's topic. */
  topicId?: string
  /** Container override — e.g. 'card py-12' where the page uses a card shell. */
  className?: string
}) {
  const { pathname } = useLocation()
  const [helpOpen, setHelpOpen] = useState(false)
  const resolvedTopic = topicId ?? resolveHelpTopicId(pathname)
  const showLearnMore = !!resolvedTopic && hasHelpTopic(resolvedTopic)

  return (
    <div className={clsx(
      'flex flex-col items-center justify-center text-muted text-center',
      className ?? 'py-16',
    )}>
      {Icon && <Icon size={48} className="mb-3 opacity-40" />}
      <p className="font-medium">{status}</p>
      <p className="text-sm mt-1 max-w-xs">{cue}</p>
      {action && <div className="mt-3">{action}</div>}
      {showLearnMore && (
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          className="mt-3 flex items-center gap-1 text-xs text-gray-400 hover:text-white light:hover:text-fg min-h-[44px] px-2">
          <HelpCircle size={13} /> Learn more
        </button>
      )}
      {helpOpen && <HelpDrawer topicId={resolvedTopic} onClose={() => setHelpOpen(false)} />}
    </div>
  )
}
